import { Grid, List, ListItem, Theme, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../app/hooks/hooks';
import { getPartnerId, getServicingOpCo } from '../../../app/store/combinedSelectors';
import { updateStageState } from '../../../app/store/EligibilityService/EligibilityServiceSlice';
import { goToClaimsBtn, goToEmergencyServices, skipBtn } from '../../../Constants/Constants';
import { PostMessageCodes } from '../../../Constants/PostMessageCodes';
import { Features, OpCo, PartnerId, ServiceType } from '../../../types/type';
import { getNextView, getStagesInOrder, postMessageToParentApp } from '../../../utils/Utilities';
import CallToAction from '../../shared/CallToAction/CallToAction';
import ModalLinearActivityIndicator from '../../shared/ModalLinearActivityIndicator/ModalLinearActivityIndicator';
import PropertyServiceInfo from '../../shared/PropertyServiceInfo/PropertyServiceInfo';
import StepProgressBar from '../../shared/StepProgressBar/StepProgressBar';

export default function EmergencyServicesHome() {
  const theme: Theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [modalLoadingOpen, setModalLoadingOpen] = React.useState(false);
  const headerContent = 'Protect your home with Emergency Services';
  const availableProgressStages = useAppSelector((state) => state.services.availableProgressStages);
  const claimSummary = useAppSelector((state) => state.claimDetail.claimSummary);
  const opco = useSelector(getServicingOpCo);
  const partnerId = useSelector(getPartnerId);

  // Get the rest of stages excluding the current stage. Find the next stage which is valid
  const nextStage = getStagesInOrder(
    availableProgressStages.filter(
      (stage) => stage.id !== Features.EMERGENCY_SERVICES && !stage.completed && stage.isEligible && !stage.skipped
    )
  )[0];

  function handleNext() {
    navigate('/emergency-services-selection', {
      state: {
        headerContent: 'Select an Emergency Service Partner',
        serviceType: ServiceType.EMERGENCY_SERVICE_TYPE,
      },
    });
  }

  function handleSkip() {
    //Update the current stage state and prepare the next stage state.
    const updatedStages = availableProgressStages.map((stage) => {
      stage =
        stage.id === Features.EMERGENCY_SERVICES
          ? {
              ...stage,
              completed: false,
              viewActive: false,
              showProgress: true,
              skipped: true,
            }
          : stage;
      stage =
        stage.id === nextStage.id
          ? {
              ...stage,
              completed: false,
              viewActive: true,
              showProgress: false,
            }
          : stage;
      return stage;
    });
    dispatch(updateStageState({ updatedStages: updatedStages }));
    navigate(getNextView(nextStage.id));
  }

  async function goToClaimsOverview() {
    if (partnerId?.toUpperCase() === PartnerId.AFICS) {
      setModalLoadingOpen(true);
      postMessageToParentApp(
        PostMessageCodes.DIGITAL_CLAIM_POST_FNOL_GO_TO_CLAIMS,
        undefined,
        undefined,
        claimSummary?.policyNumber
      );
    } else if (opco === OpCo.HOMESITE || opco === OpCo.AMFAM) {
      navigate('/claims-overview', {
        state: {
          isClaimRefreshNeeded: true,
        },
      });
    } else {
      navigate('/claim-detail', {
        state: {
          isClaimDetailRefreshNeeded: true,
        },
      });
    }
  }

  return (
    <Box sx={{ width: '100%' }}>
      <StepProgressBar stateInfo={availableProgressStages}></StepProgressBar>
      <PropertyServiceInfo
        headerContent={headerContent}
        infoContent={
          <>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                Your claim qualifies for Emergency Services. Our trusted partners protect your home, with high quality
                day or night emergency services, before repairs can take place with tarping and boarding up services.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ marginTop: '20px' }} variant="body1">
                Partners & contractors in our program are:
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <List sx={theme.custom.styles.confirmationList}>
                <ListItem sx={theme.custom.styles.confirmationListElement}>
                  <Typography variant="body1">Certified through background checks</Typography>
                </ListItem>
                <ListItem sx={theme.custom.styles.confirmationListElement}>
                  <Typography variant="body1">Fully insured</Typography>
                </ListItem>

                <ListItem sx={theme.custom.styles.confirmationListElement}>
                  <Typography variant="body1">Properly licensed</Typography>
                </ListItem>

                <ListItem sx={theme.custom.styles.confirmationListElement}>
                  <Typography variant="body1">Financially stable</Typography>
                </ListItem>
              </List>
            </Grid>
            <Grid sx={{ marginTop: '20px' }} item xs={12}>
              <Typography variant="body1">
                By selecting <b>Go to Emergency Services</b> you will be asked to choose a contract partner to provide
                these emergency services.
              </Typography>
            </Grid>
          </>
        }
      ></PropertyServiceInfo>
      {modalLoadingOpen && <ModalLinearActivityIndicator />}
      <Grid
        container
        sx={{
          width: '100%',
          maxWidth: '1280px',
          minWidth: '310px',
          margin: '0 auto',
          marginTop: '1em',
        }}
      >
        <Grid item xs={12}>
          <CallToAction
            nextButton={true}
            nextButtonContent={goToEmergencyServices}
            handleNext={handleNext}
            backButton={false}
            skipButton={true}
            skipButtonContent={skipBtn}
            handleSkip={handleSkip}
            cancelButton={true}
            cancelButtonContent={goToClaimsBtn}
            handleCancel={goToClaimsOverview}
          ></CallToAction>
        </Grid>
      </Grid>
    </Box>
  );
}
